import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  Fade,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { Refresh, Download } from '@mui/icons-material';
import MembershipPlansTab from '../components/membership/MembershipPlansTab';
import SubscriptionsTab from '../components/membership/SubscriptionsTab';
import MembershipAnalyticsTab from '../components/membership/MembershipAnalyticsTab';
import '../styles/minimal-padding.css';

const Membership: React.FC = () => {
  const [activeTab, setActiveTab] = useState('plans');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Membership Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Manage membership plans, subscriptions, and analytics
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Report" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      <Fade in timeout={700} style={{ transitionDelay: '300ms' }}>
        <Paper 
          sx={{ 
            width: '100%',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="membership management tabs"
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    minHeight: 64,
                    '&.Mui-selected': {
                      color: '#E8B4B8',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#E8B4B8',
                    height: 3,
                  },
                }}
              >
                <Tab label="Membership Plans" value="plans" />
                <Tab label="Subscriptions" value="subscriptions" />
                <Tab label="Analytics" value="analytics" />
              </Tabs>
            </Box>
            
            <TabPanel value="plans" sx={{ p: 3 }}>
              <MembershipPlansTab />
            </TabPanel>
            
            <TabPanel value="subscriptions" sx={{ p: 3 }}>
              <SubscriptionsTab />
            </TabPanel>
            
            <TabPanel value="analytics" sx={{ p: 3 }}>
              <MembershipAnalyticsTab />
            </TabPanel>
          </TabContext>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Membership;